import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { PromoCard } from '@/components/Elements/PromoCard';
import { PromoUpdateCard } from '@/components/Elements/PromoUpdateCard';
import {
  DEFAULT_CURRENCY_RESOLUTION_FOR_METERED,
  USAGE_LICENSED,
  USAGE_METERED,
  USD,
} from '@/config/appConstants';
import { useMe } from '@/hooks/useMe';
import { BillingMode, Currency, PricesTypes, ProductTypes, UserProductTypes } from '@/types';
import { getProductPrice, isAppSumo, isCoreProduct, parseCurrency } from '@/utils/helpers';

export type ProductDetailProps = {
  userProducts?: UserProductTypes[] | null;
  product?: ProductTypes | null;
  userCurrency: Currency | null;
  urlMoreInformation: string;
  billingMode: BillingMode;
};

export const ProductDetail = ({
  userProducts,
  product,
  userCurrency,
  urlMoreInformation,
  billingMode,
}: ProductDetailProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'ProductsFeatures.';
  const { getMe } = useMe();
  const me = getMe();
  userCurrency = isAppSumo(me) ? (USD as Currency) : (userCurrency as Currency);

  const generateProductsFeaturesTranslation = (text: string): string => {
    let _item = t(`${prefix}${text}`);
    _item.startsWith(prefix) && (_item = _item.replace(prefix, ''));

    return _item;
  };

  const getBullets = (product: ProductTypes) => {
    return product.info.features.bullets.map((bullet: string, index: any) => (
      <li key={index} className="pl-0.5 mb-1">
        {generateProductsFeaturesTranslation(bullet)}
      </li>
    ));
  };

  const getProductName = (product: ProductTypes) => {
    return product.info.productName;
  };

  const getCategoryName = (product: ProductTypes, billingMode: BillingMode): string => {
    if (!isCoreProduct(product)) return `${product.info.categoryName}`.toUpperCase();

    if (isAppSumo(me))
      return `${t('ProductDetail.productCategory')} ${product.info.categoryName}`.toUpperCase();

    return (
      `${t('ProductDetail.productCategory')} ` +
      `${product.info.categoryName} (${t(`BillingMode.${billingMode}ly`)})`.toUpperCase()
    );
  };

  if (!product || !userCurrency) {
    return (
      <div role="list" aria-label="ProductDetail" className="bg-white mt-1 lg:mt-[92px]">
        {userProducts && userProducts.length > 0 ? <PromoUpdateCard /> : <PromoCard />}
      </div>
    );
  }

  const productLicensedPriceObj = getProductPrice(
    product,
    USAGE_LICENSED,
    userCurrency,
    billingMode
  ) as PricesTypes;

  const productPriceMeteredObj = getProductPrice(
    product,
    USAGE_METERED,
    userCurrency,
    billingMode
  ) as PricesTypes;

  const { amount: productLicensedPrice } = productLicensedPriceObj;
  const { amount: productPriceMetered } = productPriceMeteredObj;

  const bullets = getBullets(product);
  const productName = getProductName(product);

  const parsePriceWithCurrency = () => {
    const price = (productLicensedPrice || 0) as number | bigint;
    return parseCurrency(i18n.language, userCurrency as Currency, price);
  };

  const parseMeteredWithCurrency = () => {
    const price = (productPriceMetered || 0) as number | bigint;

    return parseCurrency(
      i18n.language,
      userCurrency as Currency,
      price,
      DEFAULT_CURRENCY_RESOLUTION_FOR_METERED
    );
  };

  return (
    <div className="side__product__detail w-full border-2 border-md-blue m-auto overflow px-4 py-6">
      <div className="text-lg font-bold uppercase text-black">{t('ProductDetail.title')}</div>
      <div className="py-5 border-t-2 border-md-mediumGray">
        <div className="flex justify-between">
          <div className="font-bold">{getCategoryName(product, billingMode)}</div>
          <div className="font-black">{parsePriceWithCurrency()}</div>
        </div>
        <div className="text-xs mt-4">
          <ol className="list-disc ml-7">{bullets}</ol>
        </div>
      </div>
      <div className="py-3 mb-3">
        <div className="font-bold">{productName}</div>
      </div>
      <div className="py-4 border-t-2 border-md-mediumGray flex justify-between">
        <div>{t('ProductDetail.totalText')}</div>
        <div className="font-black">{parsePriceWithCurrency()}</div>
      </div>

      {productPriceMetered && (
        <>
          <div className="py-5 border-t-2 border-md-mediumGray text-xs">
            <Trans
              components={{ bold: <strong /> }}
              values={{ extraPrice: parseMeteredWithCurrency() }}
            >
              ProductDetail.extra
            </Trans>
          </div>
          <div>
            <a href={urlMoreInformation} title={t('ProductDetail.linkMore')} className="text-xs">
              {t('ProductDetail.linkMore')}
            </a>
          </div>
        </>
      )}
    </div>
  );
};
